import React, {
  useRef,
  useMemo,
  useState,
  Dispatch,
  useEffect,
  forwardRef,
  SetStateAction,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { useSelector } from 'react-redux';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { QueryBuilderMaterial } from '@react-querybuilder/material';
import type { OptionGroup, RuleGroupType } from 'react-querybuilder';
import { QueryBuilder as ReactQueryBuilder } from 'react-querybuilder';

import {
  removeAIThreshold,
  countFieldsInGroups,
  mergeValueLabelGroups,
  hasSpecialExecutionPeriod,
  transformDSPMetadataToLeft,
} from './helper';
import {
  RootState,
  ValueLabel,
  ValueLabelGroup,
  AlarmDefinition,
  DataSourceAlarmMetadata,
  SelectedQueryBuilderField,
} from 'types/types';
import { RulesFormValidationError } from '../CreateForm/CreateForm';
import { RulesElementsRef } from '../CreateForm/FormRules/RulesElements';
import { Ref as AlarmMangementOutputRef } from '../CreateForm/Output/Output';

import CustomCombinator from './components/CustomCombinator';
import CustomValueEditor from './components/CustomValueEditor';
import CustomDeleteButton from './components/CustomDeleteButton';
import CustomFieldSelector from './components/CustomFieldSelector';
import CustomOperatorSelector from './components/CustomOperatorSelector';

import 'react-querybuilder/dist/query-builder.css';

const muiTheme = createTheme();

export enum QueryBuilderType {
  TARGETCRITERIA = 'targetCriteria',
  Rules = 'rules',
}

const initialQuery: RuleGroupType = { combinator: 'and', rules: [] };
interface Props {
  disabled?: boolean;
  fields: OptionGroup[];
  onChange?: () => void;
  type: QueryBuilderType;
  defaultOperators: OptionGroup[];
  queryValue?: RuleGroupType | null;
  maxDistinctRulesValidation?: Dispatch<
    SetStateAction<RulesFormValidationError>
  >;
  aggregationLevelsForDimAttributes?: string[];
  alarmDefinitionData?: AlarmDefinition | null;
  toggleStudyInterval?: (show: boolean) => void;
  handleUpdateSQLQuery?: (query: RuleGroupType) => void;
  toggleUseDayOverDayIntervals?: (show: boolean) => void;
  selectedRulesRef: React.RefObject<SelectedQueryBuilderField>;
  dataSourceMetadataForAlarm: DataSourceAlarmMetadata | undefined;
  alarmRulesRef?: React.MutableRefObject<RulesElementsRef | null>;
  alarmOutputRef: React.MutableRefObject<AlarmMangementOutputRef | null>;
}

export interface Ref {
  query: RuleGroupType;
  handleResetQuery: () => void;
  container: HTMLDivElement | null;
  transformQueryDataToOutput: () => void;
  isNumberOfDistinctRuleValid: React.RefObject<string>;
}

const QueryBuilder: ForwardRefRenderFunction<Ref, Props> = (
  {
    type,
    fields,
    onChange,
    queryValue,
    alarmRulesRef,
    alarmOutputRef,
    selectedRulesRef,
    disabled = false,
    defaultOperators,
    toggleStudyInterval,
    alarmDefinitionData,
    handleUpdateSQLQuery,
    dataSourceMetadataForAlarm,
    maxDistinctRulesValidation,
    toggleUseDayOverDayIntervals,
    aggregationLevelsForDimAttributes,
  },
  ref
) => {
  const [query, setQuery] = useState<RuleGroupType>(initialQuery);
  const containerRef = useRef<HTMLDivElement>(null);
  const numberOfDistinctRuleRef = useRef<Record<string, number>>({ '': 0 });
  const isNumberOfDistinctRuleValidRef = useRef<string>('');
  const maxDistinctRulesCount = useSelector(
    (state: RootState) =>
      state.applicationState.applicationSettings
        .ALARM_MAX_NUMBER_OF_DISTINCT_RULE_ELEMENTS
  );

  //since the fields data can be a huge amount and won't change, we used memo for better performance.
  const memoizedFields = useMemo(() => {
    const isAIThresholdEnabled =
      dataSourceMetadataForAlarm?.hierarchyLevels.find(
        (s) =>
          s.id === alarmRulesRef?.current?.refs.aggregationLevel.current?.value
      )?.aiThresholdEnabled;
    if (isAIThresholdEnabled) {
      return fields;
    } else {
      return removeAIThreshold(fields);
    }
  }, [fields, dataSourceMetadataForAlarm?.hierarchyLevels, alarmRulesRef]);

  const groupsList = useMemo(() => {
    return memoizedFields.flatMap((s) =>
      s.options
        .map((option) => ({ name: option.name, label: option.label }))
        .filter(
          (option): option is { name: string; label: string } =>
            option.name !== undefined && option.label !== undefined
        )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedFields]);

  const leftOutputList = useMemo(() => {
    return dataSourceMetadataForAlarm
      ? transformDSPMetadataToLeft({
          ...dataSourceMetadataForAlarm,
          dimAttributes: dataSourceMetadataForAlarm.dimAttributes.filter((s) =>
            aggregationLevelsForDimAttributes?.includes(s.hierarchyLevelId)
          ),
        })
      : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceMetadataForAlarm, aggregationLevelsForDimAttributes]);

  const handleResetQuery = () => {
    setQuery(initialQuery);
  };
  const createValueLabel = (
    index: number,
    value: string,
    label: string
  ): ValueLabel => ({
    index,
    value,
    label,
    enableDeleteButton: false,
  });

  const createValueLabelObject = (): ValueLabelGroup[] => {
    const valueLabelGroups: ValueLabelGroup[] = [];

    if (numberOfDistinctRuleRef.current && dataSourceMetadataForAlarm) {
      const dimAttributesLevelsItems = dataSourceMetadataForAlarm.dimAttributes
        .filter((attribute) =>
          Object.keys(numberOfDistinctRuleRef.current).includes(attribute.id)
        )
        .map((level, index) => createValueLabel(index, level.id, level.name));

      if (dimAttributesLevelsItems.length > 0) {
        valueLabelGroups.push({
          group: 'Dim Attributes',
          items: dimAttributesLevelsItems,
        });
      }
    }
    return valueLabelGroups;
  };

  const transformQueryDataToOutput = () => {
    const valueLabelGroups = createValueLabelObject();

    const rightItems =
      alarmOutputRef.current?.refs.virtualizedTransferList.current?.rightListRef
        .current?.items ?? [];

    if (valueLabelGroups.length > 0) {
      const filteredRightResult = mergeValueLabelGroups(
        rightItems,
        valueLabelGroups,
        false,
        selectedRulesRef.current ?? undefined
      );

      const filteredLeftResult = mergeValueLabelGroups(
        leftOutputList,
        filteredRightResult,
        true,
        undefined
      );

      const alarmOutput =
        alarmOutputRef.current?.refs.virtualizedTransferList.current;

      alarmOutput?.rightListRef.current?.handleChangeItems(filteredRightResult);
      alarmOutput?.leftListRef.current?.handleChangeItems(filteredLeftResult);
    }
  };

  const handleDisableButtons = (): boolean => {
    return true;
  };

  useImperativeHandle(ref, () => ({
    query,
    handleResetQuery,
    container: containerRef.current,
    isNumberOfDistinctRuleValid: isNumberOfDistinctRuleValidRef,
    transformQueryDataToOutput: transformQueryDataToOutput,
  }));

  useEffect(() => {
    numberOfDistinctRuleRef.current = countFieldsInGroups(
      groupsList.map((x) => x.name),
      query
    );

    if (selectedRulesRef.current) {
      if (type === QueryBuilderType.Rules) {
        selectedRulesRef.current.rules =
          Object.keys(numberOfDistinctRuleRef.current) ?? [];
      }
    }
    transformQueryDataToOutput();

    if (maxDistinctRulesValidation) {
      let firstExceededField;
      firstExceededField = Object.entries(
        numberOfDistinctRuleRef.current || {}
      ).find(([field, count]) => count >= +maxDistinctRulesCount + 1)?.[0];
      let fieldLabel = groupsList.find(
        (x) => x.name == firstExceededField
      )?.label;
      if (firstExceededField) {
        maxDistinctRulesValidation((prev) => ({
          ...prev,
          queryBuilder: `Maximum amount of ${fieldLabel} field reached.`,
        }));
        isNumberOfDistinctRuleValidRef.current = fieldLabel ?? '';
      } else {
        maxDistinctRulesValidation((prev) => ({
          ...prev,
          queryBuilder: '',
        }));
        isNumberOfDistinctRuleValidRef.current = '';
      }
    }

    if (toggleStudyInterval && toggleUseDayOverDayIntervals) {
      const hasQuerySpecialExecutionPeriod = hasSpecialExecutionPeriod(query);
      toggleStudyInterval(hasQuerySpecialExecutionPeriod);
      if (
        !['weekly', 'monthly'].includes(
          alarmDefinitionData?.time_aggregation_level.toLowerCase() ?? ''
        )
      ) {
        toggleUseDayOverDayIntervals(hasQuerySpecialExecutionPeriod);
      } else {
        toggleUseDayOverDayIntervals(false);
      }
    }
    if (handleUpdateSQLQuery) {
      handleUpdateSQLQuery(query);
    }
    if (onChange) {
      onChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, groupsList]);

  useEffect(() => {
    if (queryValue) {
      setQuery(queryValue);
    }
  }, [queryValue]);

  return (
    <div className="query-builder-container" ref={containerRef}>
      <ThemeProvider theme={muiTheme}>
        <QueryBuilderMaterial>
          <ReactQueryBuilder
            query={query}
            addRuleToNewGroups
            disabled={disabled}
            fields={memoizedFields}
            onQueryChange={setQuery}
            onAddRule={handleDisableButtons}
            onAddGroup={handleDisableButtons}
            controlElements={{
              valueEditor: CustomValueEditor,
              fieldSelector: CustomFieldSelector,
              combinatorSelector: CustomCombinator,
              removeRuleAction: CustomDeleteButton,
              removeGroupAction: CustomDeleteButton,
              operatorSelector: CustomOperatorSelector,
            }}
            controlClassnames={{
              rule: 'custom-rule',
              addRule: 'custom-add-rule',
              addGroup: 'custom-add-group',
              ruleGroup: 'custom-rule-group',
              removeRule: 'custom-remove-rule',
              queryBuilder: 'queryBuilder-branches',
            }}
            operators={defaultOperators ? defaultOperators : undefined}
          />
        </QueryBuilderMaterial>
      </ThemeProvider>
    </div>
  );
};

export default forwardRef<Ref, Props>(QueryBuilder);
